import React, { useEffect, useState, BaseSyntheticEvent } from 'react';
//
import MarkerSearchBox from 'components/formula-entry/marker-search-box';
import { ModalContainerDark, OptionModal } from 'components/modal-provider/modal';
import {
  ButtonContainer,
  CancelButton,
  CloseIcon,
  Container,
  LargeSection,
  Content,
  Label,
  SaveFormulaButton,
  Title,
  TitleContainer,
  NoDealsMessage,
} from 'components/formula-entry/modals/_common';
import Select from 'components/input/select';
import MenuItem from 'components/menu-item';
import { handleError } from 'lib/helpers/error-handler';
import messages from 'constants/messages';
import { IDeal, ESearchFilter } from 'store/deals/types';
import { SearchHeader, SelectContainer } from 'pages/compare/components';
import { filterDealsBySearchKeyword } from 'store/deals/helpers';
import { getTokenPayload } from 'lib/auth';
import { removeUserGroup } from 'lib/helpers/name-formatters';
import ResultListToggle from 'components/result-list-toggle';
import ResultListToggleItem from 'components/result-list-toggle-item';
import labels from 'constants/labels';
import { IAccessTokenPayload } from 'lib/auth/types';

interface ICompareModalProps {
  addDealToCompare: () => void;
  availableDeals: IDeal[];
  closeModal: () => void;
  compareDealIds: (string | undefined)[];
  setCompareDealIds: (compareDealIds: (string | undefined)[]) => void;
}

const getFilterType = () => {
  return localStorage.getItem('searchfilter') === ESearchFilter.ALL ? ESearchFilter.ALL : ESearchFilter.MINE;
};

function populateSearchFilter() {
  return localStorage.getItem('searchfilter') === null ? ESearchFilter.ALL : getFilterType();
}

function getEmployeeName(idTokenResponse: IAccessTokenPayload | undefined) {
  return idTokenResponse ? removeUserGroup(idTokenResponse.name).toLowerCase() : null;
}

export default ({
  addDealToCompare,
  availableDeals,
  closeModal,
  compareDealIds,
  setCompareDealIds,
}: ICompareModalProps) => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [searchFilter, setSearchFilter] = useState(populateSearchFilter());
  const [filteredDeals, setFilteredDeals] = useState<IDeal[]>([]);
  const [dealIdsToAdd, setDealIdsToAdd] = useState<(string | undefined)[]>(compareDealIds);

  // Initial config
  useEffect(() => {
    if (isInitialized) {
      return;
    }
    setFilteredDeals(availableDeals);
    setIsInitialized(true);
  }, [availableDeals, isInitialized]);

  // Update filtered deals
  useEffect(() => {
    if (!availableDeals) {
      handleError(messages.errors.noMarkers);
      return;
    }
    // Search all field visible on dashboard table
    const fields = ['createdBy', 'customer', 'name'];
    const idTokenResponse = getTokenPayload();
    const employeeName = getEmployeeName(idTokenResponse);
    const filteredDealsNew = filterDealsBySearchKeyword(
      employeeName,
      availableDeals,
      fields,
      searchFilter,
      searchKeyword
    );
    setFilteredDeals(filteredDealsNew);
  }, [availableDeals, searchKeyword, searchFilter]);

  // Update marker value and filtered list
  function handleSearchKeywordChange(e: React.ChangeEvent<HTMLInputElement>) {
    const val = e.target.value;
    setSearchKeyword(val);
  }

  // Handle clear button for marker search input
  function handleSearchKeywordClear() {
    setSearchKeyword('');
    setFilteredDeals(availableDeals);
  }

  function handleAddToCompare() {
    if (!dealIdsToAdd.length) {
      closeModal();
      return;
    }
    addDealToCompare();
  }

  function formatDealFields(deal: IDeal): string {
    const { name, createdBy, customer } = deal;
    return `${name} / ${customer} / ${removeUserGroup(createdBy)}`;
  }

  function toggleDealSelect(dealId?: string) {
    if (dealId === undefined) {
      return;
    }

    let indexFound = -1;
    dealIdsToAdd.some((dealIdToAdd, index) => {
      if (dealIdToAdd === dealId) {
        indexFound = index;
        return true;
      }
      return false;
    });
    const dealIdsToAddUpdated = [...dealIdsToAdd];
    if (indexFound > -1) {
      dealIdsToAddUpdated.splice(indexFound, 1);
    } else {
      dealIdsToAddUpdated.push(dealId);
    }
    setDealIdsToAdd(dealIdsToAddUpdated);
    setCompareDealIds(dealIdsToAddUpdated);
  }

  return (
    <ModalContainerDark>
      <OptionModal>
        <Container>
          <TitleContainer>
            <Title>Add Deal to Compare</Title>
            <CloseIcon onClick={closeModal} />
          </TitleContainer>

          <Content>
            <LargeSection>
              <Label>SEARCH FOR DEAL</Label>
              <SearchHeader>
                <SelectContainer>
                  <Select
                    value={searchFilter}
                    onChange={(e: BaseSyntheticEvent) => {
                      localStorage.setItem('searchfilter', e.target.value);
                      setSearchFilter(e.target.value);
                    }}
                  >
                    <MenuItem value={ESearchFilter.ALL}>All</MenuItem>
                    <MenuItem value={ESearchFilter.MINE}>Mine</MenuItem>
                  </Select>
                </SelectContainer>
                <MarkerSearchBox
                  value={searchKeyword}
                  onChange={handleSearchKeywordChange}
                  onClear={handleSearchKeywordClear}
                />
              </SearchHeader>
              <ResultListToggle>
                {filteredDeals.length ? (
                  <>
                    {filteredDeals.map(f => {
                      const checked = dealIdsToAdd.some(did => did === f.id);
                      return (
                        <ResultListToggleItem
                          key={`deal-${f.id}`}
                          description={formatDealFields(f)}
                          selected={checked}
                          onClick={() => toggleDealSelect(f.id)}
                        />
                      );
                    })}
                  </>
                ) : (
                  <NoDealsMessage>No deals</NoDealsMessage>
                )}
              </ResultListToggle>
            </LargeSection>
          </Content>

          <ButtonContainer>
            <CancelButton onClick={closeModal}>Cancel</CancelButton>
            <SaveFormulaButton
              disabled={!dealIdsToAdd.length || dealIdsToAdd.length > labels.comparisonLimit}
              onClick={handleAddToCompare}
            >
              Add to Compare
            </SaveFormulaButton>
          </ButtonContainer>
        </Container>
      </OptionModal>
    </ModalContainerDark>
  );
};
